import React, { useEffect, useMemo } from "react";
import { Grid, Checkbox, FormControlLabel } from "@mui/material";
import { FastField, Field, useFormikContext } from "formik";
import { TextField } from "@mui/material";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import FacesSelector from "../../atoms/Inputs/FacesSelector.tsx";

export default function ProductPaperForm() {
  const { values, setFieldValue } = useFormikContext() as any;

  const lengthOrWeightIdx = useMemo(() => {
    const props = values.props || [];
    const i = props.findIndex(
      (p: { key?: string }) => p?.key === "L" || p?.key === "P"
    );
    return i >= 0 ? i : 3;
  }, [values.props]);

  const isBobina = values.props?.[lengthOrWeightIdx]?.key === "P";

  const handleBobinaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setFieldValue(`props.${lengthOrWeightIdx}.key`, checked ? "P" : "L");
    setFieldValue(`props.${lengthOrWeightIdx}.value`, "");
  };

  // Update props.4.value whenever thickness or thickness_unit changes
  const handleThicknessChange = () => {
    const concatenatedValue = `${values.thickness || ""}${values.thickness_unit?.toUpperCase() || ""}`;
    setFieldValue("props.4.value", concatenatedValue);
  };

  useEffect(() => {
    // Initialize props structure for PAPEL products
    if (!values.props || values.props.length === 0 ||
        !values.props.some(
          (p: { key?: string }) =>
            p?.key === "F" ||
            p?.key === "NC" ||
            p?.key === "A" ||
            p?.key === "L" ||
            p?.key === "P" ||
            p?.key === "E" ||
            p?.key === "C" ||
            p?.key === "S" ||
            p?.key === "U"
        )) {
      const props = [
        { key: "F", value: "" }, // Acabado
        { key: "NC", value: "" }, // Numero de caras
        { key: "A", value: "" }, // Ancho
        { key: "L", value: "" }, // Largo
        { key: "E", value: "" }, // Espesor
        { key: "C", value: "" }, // Color
        { key: "S", value: "" }, // Especificacion
        { key: "U", value: "" }, // Unidad de medida
      ];
      setFieldValue("props", props);
    }
  }, [setFieldValue, values.props]);

  useEffect(() => {
    handleThicknessChange();
  }, [values.thickness, values.thickness_unit]); // Trigger update when either value changes


  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FastField
          fullWidth
          name="props.0.value"
          label="Acabado"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ catalogueName: "PRODUCT_FINISH", pageSize: 1000 }}
          keyField="id"
        />
      </Grid>

      <Grid item xs={12}>
        <FacesSelector name="props.1.value" />
      </Grid>

      <Grid item xs={12}>
        <Field
          fullWidth
          name="props.2.value"
          label="Ancho (cm)"
          as={LGNumber}
          inputProps={{ step: "0.1" }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isBobina}
              onChange={handleBobinaChange}
              color="primary"
            />
          }
          label="Es bobina?"
        />
      </Grid>

      <Grid item xs={12}>
        <Field
          fullWidth
          name={`props.${lengthOrWeightIdx}.value`}
          label={isBobina ? "Peso de la bobina (kg)" : "Largo (cm)"}
          as={LGNumber}
          inputProps={{ step: "0.1" }}
        />
      </Grid>

      <Grid item xs={6}>
        <Field
          fullWidth
          name="thickness"
          label="Espesor"
          as={LGNumber}
          inputProps={{ step: "0.1" }}
          onChange={(e) => {
            setFieldValue("thickness", e.target.value);
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <FastField
          fullWidth
          name="thickness_unit"
          label="Unidad de Espesor"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ catalogueName: "UNIT", pageSize: 1000 }}
          keyField="code"
        />
      </Grid>

      <Grid item xs={12}>
        <FastField
          fullWidth
          name="props.5.value"
          label="Color"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ catalogueName: "PRODUCT_COLOR", pageSize: 1000 }}
          keyField="id"
        />
      </Grid>

      <Grid item xs={12}>
        <Field
          fullWidth
          name="props.6.value"
          label="Especificación"
          as={TextField}
        />
      </Grid>

      <Grid item xs={12}>
        <FastField
          fullWidth
          name="props.7.value"
          label="Unidad de medida"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ catalogueName: "UNIT", pageSize: 1000 }}
          keyField="id"
        />
      </Grid>
    </Grid>
  );
}
